import {
    AgentSelection, ComparisonAgreement, ComparisonDimension, DimensionManifest, DimensionMetricValue, DimensionMetrics,
    DimensionType
} from '../../../services/benchmarkService';
import { count, humanise } from '../BenchmarkVocabulary';
import { Qualification, WithheldIndex, qualificationsFor } from '../withheldMetrics';

/**
 * Vocabulary for an investigation benchmark: a run that re-investigated closed
 * cases with the selected agents and compared, dimension by dimension, what the
 * agent replay found and decided with what the historical record holds. A reader
 * never sees the word "dimension": each one is named as its kind — a decision, an
 * agent verdict, a red flag (the Risk Library's word for a factor) or a typology.
 *
 * Every dimension is read from the run's manifest — its label, its classes (in
 * order, with their labels and which of them flag) and who produces it — so a new
 * decision, agent or factor needs no code here. The maps below are fallbacks for
 * runs written before the manifest carried those, and the verbs that name a "yes"
 * for the decisions a reader meets first.
 */

/**
 * The two sides of every comparison, as an analyst reads them. "Historical" is what the
 * case history recorded when the case was closed; "Agent replay" is this run's agents
 * and configuration re-running the case. The API keeps institution/platform.
 */
export const SIDE_LABELS = {
    institution: 'Historical',
    platform: 'Agent replay',
    reason: 'Replay drivers',
    institutionOnly: 'Historical only',
    platformOnly: 'Replay only'
} as const;

/** What each side is, for a column header's tooltip. */
export const SIDE_DEFINITIONS = {
    institution: 'Recorded in case history when the case was closed, by the investigator or EDD reviewer.',
    platform: "This run's agents and configuration, re-running the case as of the date it opened.",
    reason: 'Why the agent replay decided as it did: the referral triggers, the agent\'s own signal, or a score against its threshold.'
} as const;

/** In the order the filter chips list them: the disagreements before the agreements. */
export const COMPARISON_AGREEMENT_ORDER: ComparisonAgreement[] = [
    'platform_only', 'institution_only', 'both_flagged', 'both_clear', 'not_comparable'
];

/** The same for a dimension with classes (an EDD rating, a disposition). */
export const CLASS_AGREEMENT_ORDER: ComparisonAgreement[] = ['disagree', 'adjacent', 'agree', 'not_comparable'];

/** Whether a dimension compares classes (ordinal or nominal) rather than yes/no. */
export const isClassDimension = (dimension: Pick<DimensionManifest, 'valueKind'>): boolean =>
    dimension.valueKind === 'ordinal' || dimension.valueKind === 'nominal';

/** The agreement kinds a dimension's rows can take, in chip order. */
export const agreementOrderFor = (dimension: Pick<DimensionManifest, 'valueKind'>): ComparisonAgreement[] =>
    isClassDimension(dimension) ? CLASS_AGREEMENT_ORDER : COMPARISON_AGREEMENT_ORDER;

/** How a dimension's "yes" reads in a sentence: "referred", "flagged", "named". */
const DECISION_VERBS: Record<string, string> = {
    'decision:edd_referral': 'referred',
    'decision:sar_draft': 'reported',
    'decision:sar_referral': 'referred for a SAR',
    'decision:relationship_action': 'restricted or exited',
    'decision:edd_disposition': 'referred for a SAR',
    'decision:edd_rating': 'rated high'
};

export function flagVerb(dimension: Pick<DimensionManifest, 'dimensionKey' | 'dimensionType' | 'classes'>): string {
    const known = DECISION_VERBS[dimension.dimensionKey];
    if (known) return known;
    if (dimension.dimensionType === 'typology') return 'named';
    if (dimension.dimensionType === 'agent' || dimension.dimensionType === 'factor') return 'flagged';
    const positive = dimension.classes[dimension.classes.length - 1];
    return positive ? humanise(positive).toLowerCase() : 'flagged';
}

export interface ComparisonAgreementMeta {
    label: string;
    color: string;
    description: string;
}

/**
 * One agreement kind, worded for its dimension. "Replay referred, historical did
 * not" says more than "platform_only", and it is the same chip on every dimension.
 */
export function comparisonAgreementMeta(
    kind: ComparisonAgreement | string,
    dimension: Pick<DimensionManifest, 'dimensionKey' | 'dimensionType' | 'classes'>
): ComparisonAgreementMeta {
    const verb = flagVerb(dimension);
    switch (kind) {
        case 'platform_only':
            return {
                label: `Replay ${verb}, historical did not`, color: '#F97316',
                description: `The agent replay ${verb} this case; the historical record did not.`
            };
        case 'institution_only':
            return {
                label: `Historical ${verb}, replay did not`, color: '#0EA5E9',
                description: `The historical record ${verb} this case; the agent replay did not.`
            };
        case 'both_flagged':
            return {
                label: `Both ${verb}`, color: '#10B981',
                description: `Both the historical record and the agent replay ${verb} this case.`
            };
        case 'both_clear':
            return {
                label: `Neither ${verb}`, color: '#64748B',
                description: `Neither the historical record nor the agent replay ${verb} this case.`
            };
        case 'agree':
            return {
                label: 'Agree', color: '#10B981',
                description: 'The historical record and the agent replay reached the same class.'
            };
        case 'adjacent':
            return {
                label: 'One step apart', color: '#FBBF24',
                description: 'The two sides reached neighbouring classes, for example HIGH against CRITICAL.'
            };
        case 'disagree':
            return {
                label: 'Disagree', color: '#F97316',
                description: 'The historical record and the agent replay reached different classes.'
            };
        case 'not_comparable':
            return {
                label: 'Not comparable', color: '#94A3B8',
                description: 'One side recorded nothing for this case, so the two cannot be compared. It is counted as coverage, never as agreement or disagreement.'
            };
        default:
            return { label: humanise(String(kind)), color: '#94A3B8', description: '' };
    }
}

/** A class code as a reader says it, for runs whose manifest carries no class labels. */
const CLASS_LABELS: Record<string, string> = {
    REFER: 'Refer',
    NO_REFER: 'Do not refer',
    SAR: 'SAR',
    NO_SAR: 'No SAR',
    flagged: 'Flagged',
    clear: 'Clear',
    named: 'Named',
    not_named: 'Not named'
};

/** A class code as a reader says it: the run's own label first. */
export const classLabel = (
    code: string | null | undefined,
    dimension?: Pick<DimensionManifest, 'classLabels'> | null
): string => (code ? dimension?.classLabels?.[code] ?? CLASS_LABELS[code] ?? humanise(code) : '—');

/** Whether a class is one that flags, for colouring it: the run says which; else the last class. */
export const isPositiveClass = (
    dimension: Pick<DimensionManifest, 'classes' | 'positiveClasses'>, code: string | null | undefined
): boolean => {
    if (!code) return false;
    if (dimension.positiveClasses?.length) return dimension.positiveClasses.includes(code);
    return dimension.classes.length > 1 && dimension.classes[dimension.classes.length - 1] === code;
};

/**
 * Why one side of a comparison is empty. Absent evidence is never shown as a
 * finding: an agent that found nothing to read is "no data", not "clear".
 */
const STATUS_LABELS: Record<string, { label: string; color: string; present: boolean | null }> = {
    found: { label: 'Produced', color: '#10B981', present: true },
    not_found: { label: 'No data to read', color: '#94A3B8', present: false },
    no_evidence: { label: 'No evidencing factor', color: '#94A3B8', present: false },
    not_collected: { label: 'Not collected', color: '#94A3B8', present: false },
    not_produced: { label: 'Not produced', color: '#94A3B8', present: false },
    not_routed: { label: 'Not run', color: '#94A3B8', present: false },
    skipped: { label: 'Skipped', color: '#94A3B8', present: false },
    not_applicable: { label: 'Not applicable', color: '#94A3B8', present: false },
    // The checklist left it out (agentSelection): never read as "clear".
    not_selected: { label: 'Not selected', color: '#94A3B8', present: false },
    // Today's external sources were not read for a historical case.
    skipped_replay: { label: 'Not consulted in replay', color: '#94A3B8', present: false },
    not_consulted: { label: 'No source consulted', color: '#FBBF24', present: null },
    missing: { label: 'Not performed', color: '#FBBF24', present: null },
    failed: { label: 'Check failed', color: '#FBBF24', present: null },
    error: { label: 'Check failed', color: '#FBBF24', present: null }
};

export const comparisonStatusMeta = (status: string | null | undefined) =>
    (status && STATUS_LABELS[status]) || { label: status ? humanise(status) : 'Not recorded', color: '#64748B', present: null };

/** How much of an item could be compared: on every replayed case, on some, or on none. */
export type CoverageBand = 'full' | 'partial' | 'none';

export const coverageBand = (dimension: Pick<ComparisonDimension, 'subjects' | 'comparable'>): CoverageBand =>
    dimension.comparable === 0 ? 'none' : dimension.comparable >= dimension.subjects ? 'full' : 'partial';

/**
 * Why an item's other cases could not be compared, in plain words, largest first.
 * A status other than `found` means the agent replay produced nothing for the case;
 * the cases left over after those are ones the historical record has nothing for.
 */
export function coverageGaps(
    dimension: Pick<ComparisonDimension, 'subjects' | 'comparable' | 'statusCounts'>
): { label: string; count: number }[] {
    const missing = Math.max(0, dimension.subjects - dimension.comparable);
    if (!missing) return [];
    const byLabel = new Map<string, number>();
    for (const [status, n] of Object.entries(dimension.statusCounts ?? {})) {
        if (status === 'found' || !n) continue;
        const label = comparisonStatusMeta(status).label;
        byLabel.set(label, (byLabel.get(label) ?? 0) + n);
    }
    const replayGaps = [...byLabel.values()].reduce((sum, n) => sum + n, 0);
    if (missing > replayGaps) byLabel.set('Not in the historical record', missing - replayGaps);
    return [...byLabel.entries()].map(([label, n]) => ({ label, count: n })).sort((a, b) => b.count - a.count);
}

/** The gaps as one line: "No data to read 20 · Not in the historical record 3". */
export const coverageGapLine = (dimension: Pick<ComparisonDimension, 'subjects' | 'comparable' | 'statusCounts'>): string =>
    coverageGaps(dimension).map((g) => `${g.label} ${count(g.count)}`).join(' · ');

/**
 * The selectable agents, by the slug a caller selects them with: a fallback for runs
 * and screens with no catalogue to hand. The run's manifest (producerLabel) and the
 * agent catalogue (/agent-catalog) name them otherwise.
 */
const AGENT_NAMES: Record<string, string> = {
    timing: 'Timing between transactions',
    amounts: 'Transaction amounts',
    fund_flow: 'Fund flow velocity',
    anomalies: 'Transaction anomalies',
    account_type: 'Account types',
    network: 'Entity network',
    sanctions: 'Restricted-party screening',
    adverse_media: 'Adverse media',
    corporate_registry: 'Corporate registry',
    web_legitimacy: 'Product & web legitimacy',
    source_of_funds: 'Source & use of funds',
    ownership: 'Ownership & beneficial owners',
    network_expansion: 'Connected-party network',
    kyc_profile: 'Declared KYC profile'
};

export const agentSlugName = (slug: string, labels?: Record<string, string> | null): string =>
    labels?.[slug] ?? AGENT_NAMES[slug] ?? humanise(slug);

/** A dimension's producer, named. A typology lists every agent that evidences it. */
export function producerLabel(
    producer: string | string[] | null | undefined, labels?: Record<string, string> | null
): string {
    if (!producer) return '—';
    const list = Array.isArray(producer) ? producer : [producer];
    return list.map((p) => agentSlugName(p, labels)).join(', ');
}

/** {agent slug: name} from a run's manifest, which names each producing agent. */
export function agentLabelsFrom(dimensions: Pick<DimensionManifest, 'producer' | 'producerLabel'>[]): Record<string, string> {
    const labels: Record<string, string> = {};
    for (const d of dimensions) {
        if (typeof d.producer === 'string' && d.producerLabel) labels[d.producer] = d.producerLabel;
    }
    return labels;
}

export const DIMENSION_TYPE_LABELS: Record<DimensionType, string> = {
    decision: 'Decision',
    agent: 'Agent verdict',
    factor: 'Red flag',
    typology: 'Typology'
};

/** The same, plural, for a picker or a heading: "Typologies", not "Typologys". */
export const DIMENSION_TYPE_PLURALS: Record<DimensionType, string> = {
    decision: 'Decisions',
    agent: 'Agent verdicts',
    factor: 'Red flags',
    typology: 'Typologies'
};

/** A dimension's kind as a noun in a sentence: "this red flag", "this decision". */
export const dimensionNoun = (dimension: Pick<DimensionManifest, 'dimensionType'>): string =>
    (DIMENSION_TYPE_LABELS[dimension.dimensionType] ?? 'item').toLowerCase();

/* ------------------------------------------------------------------ *
 * Metrics
 * ------------------------------------------------------------------ */

/** One figure, or null when the run did not record it. */
export function metric(
    metrics: DimensionMetrics | null | undefined, name: string, label = 'all'
): DimensionMetricValue | null {
    const figure = metrics?.[name]?.[label];
    return figure && figure.value !== null && figure.value !== undefined ? figure : null;
}

/**
 * Agreement beyond chance, in words (Landis & Koch). Raw agreement flatters a
 * dimension where both sides mostly say "no"; kappa is what says whether they
 * agree about the cases that matter.
 */
export function kappaReading(kappa: number | null | undefined): string | null {
    if (kappa === null || kappa === undefined || !Number.isFinite(kappa)) return null;
    if (kappa < 0) return 'worse than chance';
    if (kappa < 0.2) return 'slight';
    if (kappa < 0.4) return 'fair';
    if (kappa < 0.6) return 'moderate';
    if (kappa < 0.8) return 'substantial';
    return 'almost perfect';
}

/** One step of the investigation path the supervisor took, as the run recorded it. */
export interface RouteStep {
    node: string;
    mode?: string;
    /** The activity template's name; runs scored before it was kept have none. */
    title?: string;
}

// An org copy of a template carries the time it was copied: VERIFY_TIMING_INTERVALS_20260920T175956.
const COPY_SUFFIX = /_\d{8}T\d{6}$/;

/**
 * A step's name for a reader: its activity template name, else its code in words
 * without the copy's timestamp. The code stays available for matching a step to the flow.
 */
export const routeStepName = (step: RouteStep): string =>
    step.title || humanise(String(step.node || '').replace(COPY_SUFFIX, ''));

/**
 * Of the cases either side flagged, how many both flagged. Answers what kappa
 * answers for a rare red flag (agreement on the cases that matter, not on the
 * many both cleared) from counts the reader can check, so it leads the
 * scorecard; kappa stays in the item's figures for validators. Null when neither
 * side flagged a case.
 */
export function flagOverlap(counts: Partial<Record<ComparisonAgreement, number>>): { both: number; either: number; value: number } | null {
    const both = counts.both_flagged || 0;
    const either = both + (counts.platform_only || 0) + (counts.institution_only || 0);
    return either ? { both, either, value: both / either } : null;
}

/**
 * The dimension's result in one sentence, from its agreement counts. Says
 * nothing it cannot back: a dimension with no comparable case says so.
 */
export function dimensionSentence(dimension: ComparisonDimension): string {
    const verb = flagVerb(dimension);
    const counts = dimension.agreementCounts;
    if (!dimension.comparable) {
        return dimension.subjects
            ? `None of the ${count(dimension.subjects)} cases could be compared: one side recorded nothing for each of them.`
            : `This run compared no cases on this ${dimensionNoun(dimension)}.`;
    }
    if (isClassDimension(dimension)) {
        const same = counts.agree || 0;
        const near = counts.adjacent || 0;
        return `The historical record and the agent replay agreed on ${count(same)} of ${count(dimension.comparable)} comparable cases`
            + (dimension.valueKind === 'ordinal' ? `, and were one step apart on ${count(near)}.` : '.');
    }
    const agreed = (counts.both_flagged || 0) + (counts.both_clear || 0);
    const parts = [`The historical record and the agent replay agreed on ${count(agreed)} of ${count(dimension.comparable)} comparable cases.`];
    const platformOnly = counts.platform_only || 0;
    const institutionOnly = counts.institution_only || 0;
    if (platformOnly || institutionOnly) {
        parts.push(`The agent replay ${verb} ${count(platformOnly)} ${platformOnly === 1 ? 'case' : 'cases'} the historical record did not; the historical record ${verb} ${count(institutionOnly)} the replay did not.`);
    }
    return parts.join(' ');
}

/**
 * Caveats on one dimension: those the run raised on it by key, on its kind
 * (`agent`, `factor`, `typology`), or on the agent that produces it.
 */
export function dimensionQualifications(index: WithheldIndex, dimension: DimensionManifest): Qualification[] {
    const producers = Array.isArray(dimension.producer) ? dimension.producer : dimension.producer ? [dimension.producer] : [];
    return qualificationsFor(index, [
        dimension.dimensionKey,
        dimension.dimensionType,
        ...producers.map((p) => `agent:${p}`)
    ]);
}

/** The evidence behind a dimension's headline: its comparable cases and the run's confidence. */
export function dimensionEvidence(dimension: Pick<ComparisonDimension, 'metrics' | 'comparable'>) {
    const agreement = metric(dimension.metrics, 'agreementRate') ?? metric(dimension.metrics, 'exactAgreement');
    return {
        sampleSize: agreement?.sampleSize ?? dimension.comparable,
        confidence: agreement?.confidence ?? null
    };
}

/**
 * Why the platform's side of one case is what it is, in a few words: the
 * referral's triggers, an agent's own summary, or a factor's score against its
 * threshold. Empty when the comparison recorded nothing to explain it.
 */
export function platformReason(
    detail: Record<string, unknown> | null | undefined,
    platformScore: number | null | undefined,
    dimension: Pick<DimensionManifest, 'flagAt'>
): string {
    const parts: string[] = [];
    const triggers = Array.isArray(detail?.triggers) ? (detail!.triggers as unknown[]) : [];
    if (triggers.length) parts.push(triggers.map((t) => humanise(String(t))).join(', '));
    if (detail?.mandatory === true) parts.push('mandatory');
    if (typeof detail?.signal === 'string' && detail.signal.trim()) parts.push(detail.signal.trim());
    if (platformScore !== null && platformScore !== undefined && Number.isFinite(platformScore)) {
        parts.push(dimension.flagAt !== null && dimension.flagAt !== undefined
            ? `score ${platformScore} (flags at ${dimension.flagAt})`
            : `score ${platformScore}`);
    }
    return parts.join(' · ');
}

/** Factors grouped under the agent that produces them, in manifest order. */
export function factorsByAgent(dimensions: ComparisonDimension[]): Map<string, ComparisonDimension[]> {
    const grouped = new Map<string, ComparisonDimension[]>();
    for (const d of dimensions) {
        if (d.dimensionType !== 'factor' || typeof d.producer !== 'string') continue;
        const list = grouped.get(d.producer);
        if (list) list.push(d);
        else grouped.set(d.producer, [d]);
    }
    return grouped;
}

/** What a run replayed, in the unit a reader counts: alerts, cases or EDD reviews. */
export function runSubjectsNoun(run: { benchmarkType?: string; stage?: string | null; comparisonSpec?: { stage?: string | null } | null }): string {
    const stage = run.stage ?? run.comparisonSpec?.stage ?? null;
    if (stage === 'edd') return 'EDD reviews';
    if (run.benchmarkType === 'case_investigation') return 'cases';
    return 'alerts';
}

/** A run's agent selection in a few words, for a run picker: "ownership, network (exact)". */
export function selectionLine(selection: AgentSelection | null | undefined): string | null {
    if (!selection?.agents?.length) return null;
    const names = selection.agents.map((a) => agentSlugName(a));
    const shown = names.length > 3 ? `${names.slice(0, 3).join(', ')} +${names.length - 3}` : names.join(', ');
    return `${shown}${selection.mode ? ` (${selection.mode})` : ''}`;
}
